import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatStepper } from '@angular/material/stepper';
import { ContactUsMenuComponent } from '../contact-us-menu/contact-us-menu.component';
import { EditOrderInitialMenuComponent } from '../edit-order-initial-menu/edit-order-initial-menu.component';
import { Subscription } from 'rxjs';
import { EditOrderFlowTypes, EditOrderService } from '../../../services/edit-order.service';
import { RecipientDetailsFormComponent } from '../recipient-details-form/recipient-details-form.component';
import { ChangeAddressFormComponent } from '../change-address-form/change-address-form.component';
import { EditOrderFinalMessageComponent } from '../edit-order-final-message/edit-order-final-message.component';
import { DeliveryInstructionsMenuComponent } from '../delivery-instructions-menu/delivery-instructions-menu.component';
import { EditOrderInstructionsFormComponent } from '../edit-order-instructions-form/edit-order-instructions-form.component';
import { EditOrderChangeDateTimeComponent } from '../edit-order-change-date-time/edit-order-change-date-time.component';
import { GtmTracking } from '../../../services/gtm.service';

const MainMenuTitle = 'What would you like to do?';
@Component({
  selector: 'common-edit-order-menu',
  templateUrl: './edit-order-menu.component.html',
  styleUrls: ['./edit-order-menu.component.scss'],
})
export class EditOrderMenuComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper?: MatStepper;
  isAllowedToEdit = true;
  hideBackButton = false;
  hideCloseButton = false;
  title = MainMenuTitle;
  steps: any[] = [];
  stepsMap: any = {};
  firstStep = {
    component: EditOrderInitialMenuComponent,
    inputs: { brandColor: this.data.brandColor },
  };

  //Subscriptions
  startStepperSub?: Subscription;
  nextStepSubscription?: Subscription;
  titleSubscription?: Subscription;
  hideBackSubscription?: Subscription;
  hideCloseButtonSub?: Subscription;
  closeMenuSub?: Subscription;
  showErrorSub?: Subscription;
  contactUsSub?: Subscription;
  indexChangeSub?: Subscription;
  constructor(
    private _bottomSheetRef: MatBottomSheetRef<EditOrderMenuComponent>,
    private editOrderService: EditOrderService,
    private gtmService: GtmTracking,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
  ) {}

  ngOnInit() {
    this.setSteps();
    this.setSubscriptions();
  }

  onPrevious() {
    this.gtmService.clickEvent(
      `${this.data?.contained ? 'Settings/' : ''}Tracking page/Edit order panel/${this.title}/Previous`,
    );
    this.stepper?.previous();
  }

  setSubscriptions() {
    this.nextStepSubscription = this.editOrderService.emitNextStep$.subscribe(() => {
      this.stepper?.next();
    });
    this.titleSubscription = this.editOrderService.emitTitle$.subscribe((title) => {
      this.title = title;
    });
    this.hideBackSubscription = this.editOrderService.hideBackArrow$.subscribe((val) => {
      this.hideBackButton = val;
    });
    this.hideCloseButtonSub = this.editOrderService.hideCloseButton$.subscribe((val) => {
      if (val) {
        setTimeout(() => {
          this.hideCloseButton = val;
        }, 1000);
      } else {
        this.hideCloseButton = val;
      }
    });
    this.startStepperSub = this.editOrderService.startStepper$.subscribe((action) => {
      this.initiateStepperByType(action);
    });
    this.closeMenuSub = this.editOrderService.closeMenu$.subscribe(() => {
      this.onClose();
    });
    this.showErrorSub = this.editOrderService.showErrorScreen$.subscribe(() => {
      this.steps = [
        {
          component: ContactUsMenuComponent,
          inputs: {
            brandColor: this.data.brandColor,
            merchantEmail: this.data?.merchantEmail,
            merchantPhone: this.data?.merchantPhone,
            chatAllowed: this.data.chatAllowed,
            note: 'Oops, we had an issue updating your delivery. Please contact us for assistance.',
            noteColor: '#FFE2E0',
          },
        },
      ];
      this.editOrderService.hideCloseButton.emit(false);
      //this.stepper!.selectedIndex = 0
      this.title = 'Contact us';
    });
    this.contactUsSub = this.editOrderService.contactUs$.subscribe(() => {
      this.initiateStepperByType(EditOrderFlowTypes.Contact);
    });
    this.indexChangeSub = this.editOrderService.afterIndexChange$.subscribe((val) => {
      if (val === 0) {
        this.editOrderService.hideCloseButton.emit(false);
      }
    });
  }

  ngOnDestroy() {
    this.nextStepSubscription?.unsubscribe();
    this.startStepperSub?.unsubscribe();
    this.titleSubscription?.unsubscribe();
    this.hideBackSubscription?.unsubscribe();
    this.showErrorSub?.unsubscribe();
    this.closeMenuSub?.unsubscribe();
    this.indexChangeSub?.unsubscribe();
  }

  setSteps() {
    if (this.data.allowedToEdit) {
      this.steps[0] = this.firstStep;
      this.stepsMap = {
        [EditOrderFlowTypes.Contact]: {
          steps: [
            { ...this.firstStep },
            {
              component: ContactUsMenuComponent,
              inputs: {
                brandColor: this.data.brandColor,
                merchantEmail: this.data?.merchantEmail,
                merchantPhone: this.data?.merchantPhone,
                chatAllowed: this.data.chatAllowed,
              },
            },
          ],
          title: 'Contact us',
        },
        [EditOrderFlowTypes.ChangeRecipientDetails]: {
          title: 'Change recipient details',
          steps: [
            { ...this.firstStep },
            {
              component: RecipientDetailsFormComponent,
              inputs: {
                brandColor: this.data.brandColor,
                textColor: this.data.textColor,
                order: this.data?.orderData,
                index: 1,
              },
            },
            {
              component: EditOrderFinalMessageComponent,
              inputs: { text: 'Recipient details<br>successfully changed', index: 2 },
            },
          ],
        },
        [EditOrderFlowTypes.ChangeAddress]: {
          title: 'Deliver to a different address',
          steps: [
            { ...this.firstStep },
            {
              component: ChangeAddressFormComponent,
              inputs: {
                brandColor: this.data.brandColor,
                textColor: this.data.textColor,
                order: this.data?.orderData,
                index: 1,
              },
            },
            {
              component: EditOrderFinalMessageComponent,
              inputs: { text: 'Address & delivery time<br>successfully changed', index: 2 },
            },
          ],
        },
        [EditOrderFlowTypes.AddInstructions]: {
          title: 'Leave delivery instructions',
          steps: [
            { ...this.firstStep },
            {
              component: DeliveryInstructionsMenuComponent,
              inputs: { brandColor: this.data.brandColor, textColor: this.data.textColor },
            },
            {
              component: EditOrderInstructionsFormComponent,
              inputs: { brandColor: this.data.brandColor, textColor: this.data.textColor, order: this.data?.orderData },
            },
            {
              component: EditOrderFinalMessageComponent,
              inputs: { text: 'Delivery instructions<br>successfully changed', index: 3 },
            },
          ],
        },
        [EditOrderFlowTypes.ChangeDateAndTime]: {
          title: 'Select a new delivery time',
          steps: [
            { ...this.firstStep },
            {
              component: EditOrderChangeDateTimeComponent,
              inputs: {
                brandColor: this.data.brandColor,
                textColor: this.data.textColor,
                order: this.data?.orderData,
                index: 1,
              },
            },
            {
              component: EditOrderFinalMessageComponent,
              inputs: { text: 'Delivery time<br>successfully changed', index: 2 },
            },
          ],
        },
      };
    } else {
      this.steps[0] = {
        component: ContactUsMenuComponent,
        inputs: {
          brandColor: this.data.brandColor,
          merchantEmail: this.data?.merchantEmail,
          merchantPhone: this.data?.merchantPhone,
          chatAllowed: this.data.chatAllowed,
          note: 'Online changes are not available at the moment.<br>Please contact us for assistance.',
        },
      };
    }
  }

  initiateStepperByType(type: EditOrderFlowTypes) {
    this.steps = [];
    this.steps = this.stepsMap[type]?.steps;
    setTimeout(() => {
      this.title = this.stepsMap[type]?.title;
      this.stepper?.next();
    }, 300);
  }

  onClose() {
    this.gtmService.clickEvent(
      `${this.data?.contained ? 'Settings/' : ''}Tracking page/Edit order panel/${this.title}/Close`,
    );
    this._bottomSheetRef.dismiss();
  }

  onStepChange(e: any) {
    this.editOrderService.setCurrentIndex(e.selectedIndex);
    if (e.selectedIndex === 0) {
      this.title = MainMenuTitle;
    }
  }
}
